import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import type { MessageOptions } from "./chatsSliceThunks"

type Jid = string

/** A send that reached the translate service and could not be translated. */
export type TranslationFailure = {
  /** Exactly what the dispatcher typed, so the retry sends the same thing. */
  text: string
  options: MessageOptions
}

export type ComposerTranslationState = {
  /**
   * The dispatcher paused translation for this chat from the composer. Held
   * in memory only (this slice is in PERSIST_BLACKLIST) so a reload returns
   * to the driver's own preferred language — pausing is for one conversation,
   * not a setting somebody can forget they changed.
   */
  paused?: boolean
  /** Set when a send failed to translate and is waiting on the dispatcher. */
  failure?: TranslationFailure
}

type State = Record<Jid, ComposerTranslationState>

const initialState: State = {}

const composerTranslationSlice = createSlice({
  name: "composerTranslation",
  initialState,
  reducers: {
    toggleTranslationPaused: (
      state,
      action: PayloadAction<{ chatJid: Jid }>,
    ) => {
      const { chatJid } = action.payload
      const entry = state[chatJid] || {}
      state[chatJid] = { ...entry, paused: !entry.paused }
    },
    setTranslationFailure: (
      state,
      action: PayloadAction<{ chatJid: Jid; failure: TranslationFailure }>,
    ) => {
      const { chatJid, failure } = action.payload
      state[chatJid] = { ...(state[chatJid] || {}), failure }
    },
    clearTranslationFailure: (
      state,
      action: PayloadAction<{ chatJid: Jid }>,
    ) => {
      const { chatJid } = action.payload
      const entry = state[chatJid]
      if (!entry) return
      state[chatJid] = { ...entry, failure: undefined }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const {
  toggleTranslationPaused,
  setTranslationFailure,
  clearTranslationFailure,
} = composerTranslationSlice.actions

export const selectTranslationPaused =
  (chatJid: Jid) =>
  (state: { composerTranslation: State }): boolean =>
    Boolean(state.composerTranslation[chatJid]?.paused)

export const selectTranslationFailure =
  (chatJid: Jid) =>
  (state: { composerTranslation: State }): TranslationFailure | undefined =>
    state.composerTranslation[chatJid]?.failure

export default composerTranslationSlice.reducer
